import type { MaskRect } from '@innspel/core';
import { PRIVATE_ATTRIBUTE, WIDGET_ATTRIBUTE, type Fangst, type ScreenshotAdapter } from './typer.ts';

/**
 * The package's own client-side renderer: «Ta bilde av siden» (capture the page) without a
 * single byte of the DOM leaving the browser (K4), and without a third-party dependency (K19).
 * The choice and the rejected alternatives are in tech-decisions.md, «M1.5».
 *
 * The mechanism is SVG `foreignObject`: the DOM is cloned, computed styles are written
 * as inline style on every node, the clone is placed in a `<foreignObject>` in an
 * SVG with the viewport's dimensions, the SVG is loaded as an image and drawn to a canvas.
 * Three things happen BEFORE serialisation, and they are what make the renderer safe:
 *
 * - The widget itself (`data-innspel-widget`) is removed from the clone.
 * - Private fields (`data-innspel-private`, DD-39) are emptied in the clone AND measured in
 *   the original, so they are masked in the bitmap even if the geometry should diverge.
 * - Images are replaced: same-origin ones are drawn in as a data URL, everything else becomes
 *   a neutral surface. An image from another origin taints the canvas — and is in any
 *   case not the host's own content.
 *
 * Web fonts are not loaded into the SVG image; the text falls back to the system font.
 * The layout is the same, and the layout is what has value in a screenshot.
 */

export interface Klon {
  klon: HTMLElement;
  /** Private fields in CSS pixels relative to the viewport. */
  automasker: MaskRect[];
  /** Text lines in CSS pixels relative to the viewport. */
  tekstRekter: MaskRect[];
}

const UTELATT = new Set(['SCRIPT', 'NOSCRIPT', 'STYLE', 'LINK', 'META', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'VIDEO', 'AUDIO']);

/**
 * Builds the clone to be rendered. Pure DOM, no canvas — so it can be tested in
 * jsdom: the widget is gone, private fields are empty, and their geometry is measured.
 */
export function cloneForImage(rot: HTMLElement, viewport: { width: number; height: number }): Klon {
  const automasker: MaskRect[] = [];
  const tekstRekter: MaskRect[] = [];
  const klon = klonNode(rot, rot, automasker, viewport, tekstRekter) as HTMLElement;
  return { klon, automasker, tekstRekter };
}

function klonNode(orig: Node, rot: HTMLElement, automasker: MaskRect[], viewport: { width: number; height: number }, tekstRekter: MaskRect[]): Node | null {
  if (orig.nodeType === Node.TEXT_NODE) {
    for (const r of tekstlinjer(orig as Text, viewport)) tekstRekter.push(r);
    return orig.cloneNode(false);
  }
  if (orig.nodeType !== Node.ELEMENT_NODE) return null;
  const el = orig as HTMLElement;

  if (UTELATT.has(el.tagName) || el.hasAttribute(WIDGET_ATTRIBUTE)) return null;

  const klon = el.cloneNode(false) as HTMLElement;
  inlineStil(el, klon);
  // id-er og skjemanavn skal ikke duplisere originalens — klonen lever bare i en SVG.
  klon.removeAttribute('id');

  if (el.hasAttribute(PRIVATE_ATTRIBUTE)) {
    // DD-39: innholdet er BORTE fra klonen, ikke bare dekket, og feltet måles i
    // originalen så bitmapen maskeres uansett.
    const r = synligRekt(el.getBoundingClientRect(), viewport);
    if (r) automasker.push(r);
    klon.textContent = '';
    klon.style.setProperty('color', 'transparent');
    klon.style.setProperty('background', 'var(--innspel-screen-auto-mask-fill, #101114)');
    return klon;
  }

  if (el instanceof HTMLImageElement) {
    return bildeSomFlate(el, klon);
  }
  if (el instanceof HTMLCanvasElement) {
    try {
      const data = el.toDataURL();
      const img = document.createElement('img');
      inlineStil(el, img);
      img.setAttribute('src', data);
      return img;
    } catch {
      return nøytralFlate(klon);
    }
  }
  if (el instanceof HTMLTextAreaElement) {
    klon.textContent = el.value;
    return klon;
  }
  if (el instanceof HTMLInputElement) {
    if (el.type === 'password') {
      klon.setAttribute('value', '•'.repeat(el.value.length));
    } else if (el.type === 'checkbox' || el.type === 'radio') {
      if (el.checked) klon.setAttribute('checked', '');
      else klon.removeAttribute('checked');
    } else {
      klon.setAttribute('value', el.value);
    }
    return klon;
  }
  if (el instanceof HTMLSelectElement) {
    for (const [i, opt] of Array.from(el.options).entries()) {
      const k = klon.children[i];
      if (k) {
        if (opt.selected) k.setAttribute('selected', '');
        else k.removeAttribute('selected');
      }
    }
  }

  for (const barn of Array.from(el.childNodes)) {
    const k = klonNode(barn, rot, automasker, viewport, tekstRekter);
    if (k) klon.appendChild(k);
  }
  return klon;
}

/** Same-origin image → data URL. Everything else → neutral surface (taints the canvas). */
function bildeSomFlate(img: HTMLImageElement, klon: HTMLElement): HTMLElement {
  try {
    if (!img.complete || img.naturalWidth === 0) return nøytralFlate(klon);
    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const ctx = c.getContext('2d');
    if (!ctx) return nøytralFlate(klon);
    ctx.drawImage(img, 0, 0);
    klon.setAttribute('src', c.toDataURL());
    klon.removeAttribute('srcset');
    klon.removeAttribute('loading');
    return klon;
  } catch {
    return nøytralFlate(klon);
  }
}

function nøytralFlate(klon: HTMLElement): HTMLElement {
  const flate = document.createElement('div');
  // CSSOM, ikke setAttribute: en CSP uten 'unsafe-inline' i style-src blokkerer attributtet,
  // men ikke `style.cssText` (portalens CSP, vakt 6).
  flate.style.cssText = klon.style.cssText;
  flate.style.setProperty('background', 'var(--innspel-color-neutral-bg, #EEF0F3)');
  flate.style.setProperty('display', 'inline-block');
  return flate;
}

/**
 * Writes the computed styles onto the clone. `cssText` from `getComputedStyle`
 * is empty in most browsers, so the properties are copied one by one.
 */
function inlineStil(orig: Element, klon: HTMLElement): void {
  const beregnet = getComputedStyle(orig);
  const deler: string[] = [];
  for (let i = 0; i < beregnet.length; i++) {
    const navn = beregnet[i]!;
    const verdi = beregnet.getPropertyValue(navn);
    if (verdi) deler.push(`${navn}:${verdi}`);
  }
  // CSSOM, ikke style-attributtet via setAttribute: under en CSP uten 'unsafe-inline' i style-src
  // blokkerer nettleseren attributtet stille, og bildet ble uten stiler. `cssText` er CSSOM
  // og tillatt, og XMLSerializer skriver den ut som attributt i SVG-en likevel (vakt 6).
  if (deler.length) klon.style.cssText = deler.join(';');
  klon.removeAttribute('class');
}

function tekstlinjer(node: Text, viewport: { width: number; height: number }): MaskRect[] {
  if (!node.data.trim() || typeof document.createRange !== 'function') return [];
  const range = document.createRange();
  range.selectNodeContents(node);
  // jsdom har ingen layout og ingen getClientRects; da finnes ingen tekstlinjer å måle.
  if (typeof range.getClientRects !== 'function') return [];
  const ut: MaskRect[] = [];
  for (const r of Array.from(range.getClientRects())) {
    const s = synligRekt(r, viewport);
    if (s) ut.push(s);
  }
  return ut;
}

function synligRekt(r: DOMRect | DOMRectReadOnly, viewport: { width: number; height: number }): MaskRect | null {
  const x1 = Math.max(0, r.left);
  const y1 = Math.max(0, r.top);
  const x2 = Math.min(viewport.width, r.right);
  const y2 = Math.min(viewport.height, r.bottom);
  if (x2 <= x1 || y2 <= y1) return null;
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

/** The SVG markup with the clone in a foreignObject, offset so the viewport is what is shown. */
export function serialiserSvg(klon: HTMLElement, viewport: { width: number; height: number }, scroll: { x: number; y: number }, bakgrunn: string): string {
  klon.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  const xml = new XMLSerializer().serializeToString(klon);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${viewport.width}" height="${viewport.height}">` +
    `<foreignObject width="100%" height="100%">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${viewport.width}px;height:${viewport.height}px;overflow:hidden;background:${bakgrunn}">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="transform:translate(${-scroll.x}px,${-scroll.y}px)">${xml}</div>` +
    `</div></foreignObject></svg>`
  );
}

/** The largest scale that keeps the image within 4096² (K9) — and never above the device's own. */
export function velgSkala(viewport: { width: number; height: number }, dpr: number, tak = 4096): number {
  return Math.min(dpr, tak / Math.max(viewport.width, viewport.height, 1));
}

async function tegnSvg(svg: string, viewport: { width: number; height: number }, skala: number): Promise<HTMLCanvasElement> {
  const img = new Image();
  img.decoding = 'sync';
  const lastet = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('could not render the page'));
  });
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await lastet;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width * skala);
  canvas.height = Math.round(viewport.height * skala);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas is not available');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const skalerRekt = (r: MaskRect, s: number): MaskRect => ({
  x: Math.floor(r.x * s),
  y: Math.floor(r.y * s),
  width: Math.ceil(r.width * s),
  height: Math.ceil(r.height * s),
});

/**
 * «Ta bilde av siden» (capture the page): the viewport as the submitter sees it, with the
 * widget left out and private fields masked (K4, DD-39).
 */
export async function renderPage(rot: HTMLElement = document.body): Promise<Fangst> {
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const scroll = { x: window.scrollX, y: window.scrollY };
  const { klon, automasker, tekstRekter } = cloneForImage(rot, viewport);
  const bakgrunn = getComputedStyle(document.body).backgroundColor || '#fff';
  const svg = serialiserSvg(klon, viewport, scroll, bakgrunn);
  const skala = velgSkala(viewport, window.devicePixelRatio || 1);
  const canvas = await tegnSvg(svg, viewport, skala);
  return {
    bilde: canvas,
    width: canvas.width,
    height: canvas.height,
    automasker: automasker.map((r) => skalerRekt(r, skala)),
    tekstRekter: tekstRekter.map((r) => skalerRekt(r, skala)),
    kilde: 'side',
  };
}

/** The adapter the package uses when the host does not provide its own. */
export const standardAdapter: ScreenshotAdapter = {
  capture: (rot) => renderPage(rot),
};
